import React, { useState, useEffect } from 'react';
import { fetchAdminDiaryEntries } from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import { questionnaireSections } from '../../constants/questionnaire';

const DiaryEntriesManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [expandedEntries, setExpandedEntries] = useState(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchAdminDiaryEntries();
        setUsers(data.users || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching diary entries:', err);
        setError('Failed to load diary entries');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleUser = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleEntry = (entryId) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNoteTypeLabel = (noteType) => {
    const labels = {
      'feedback': 'Feedback',
      'note': 'Poznámka',
      'question': 'Otázka'
    };
    return labels[noteType] || noteType;
  };

  // Function to render questionnaire responses
  const renderQuestionnaire = (questionnaire) => {
    if (!questionnaire || !questionnaire.responses) {
      return null;
    }

    const responses = questionnaire.responses;

    return (
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        border: '1px solid #dee2e6', 
        borderRadius: '4px', 
        padding: '15px', 
        marginTop: '10px' 
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '0.9rem' }}>Questionnaire Responses</h4>
        {questionnaireSections.map(section => (
          <div key={section.id} style={{ marginBottom: '15px' }}>
            <h5 style={{ 
              margin: '0 0 8px 0', 
              color: '#6c757d', 
              fontSize: '0.85rem', 
              fontWeight: '600',
              borderBottom: '1px solid #e9ecef',
              paddingBottom: '4px'
            }}>
              {section.id}. {section.title}
            </h5>
            <div style={{ paddingLeft: '10px' }}>
              {section.questions.map(question => {
                const response = responses[question.id];
                return (
                  <div key={question.id} style={{ marginBottom: '8px' }}>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#495057', 
                      fontWeight: '500',
                      marginBottom: '3px'
                    }}>
                      {question.text}
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#6c757d',
                      backgroundColor: '#ffffff',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      border: '1px solid #e9ecef'
                    }}>
                      {response || 'N/A'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  const totalEntries = users.reduce((sum, user) => sum + user.entries_count, 0);

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Diary Entries</h2>
        <span style={{ color: '#666', fontSize: '0.9rem' }}>
          {users.length} uživatelů, {totalEntries} záznamů celkem
        </span>
      </div>

      {users.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '40px' }}>
          Žádné diary entries nebyly nalezeny.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {users.map(user => (
            <div
              key={user.user_id}
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              {/* User header - clickable */}
              <div
                onClick={() => toggleUser(user.user_id)}
                style={{
                  padding: '15px 20px',
                  backgroundColor: expandedUsers.has(user.user_id) ? '#e9ecef' : '#f8f9fa',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    fontSize: '1.2rem',
                    transition: 'transform 0.2s ease',
                    display: 'inline-block',
                    transform: expandedUsers.has(user.user_id) ? 'rotate(90deg)' : 'rotate(0deg)'
                  }}>
                    ▶
                  </span>
                  <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50' }}>
                    {user.username}
                  </span>
                </div>
                <span style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  {user.entries_count} {user.entries_count === 1 ? 'záznam' : user.entries_count < 5 ? 'záznamy' : 'záznamů'}
                </span>
              </div>

              {/* User entries - collapsible */}
              {expandedUsers.has(user.user_id) && (
                <div style={{ padding: '0' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f3f5' }}>
                        <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: '150px' }}>Topic</th>
                        <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: '100px' }}>Typ</th>
                        <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Poznámka</th>
                        <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: '150px' }}>Vytvořeno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.entries.map(entry => (
                        <React.Fragment key={entry.id}>
                          <tr 
                            style={{ 
                              borderBottom: '1px solid #eee',
                              cursor: entry.questionnaire ? 'pointer' : 'default'
                            }}
                            onClick={entry.questionnaire ? () => toggleEntry(entry.id) : undefined}
                          >
                            <td style={{ padding: '10px 15px', verticalAlign: 'top' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {entry.topic}
                                {entry.questionnaire && (
                                  <span style={{
                                    fontSize: '0.7rem',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    padding: '2px 5px',
                                    borderRadius: '3px',
                                    fontWeight: 'bold'
                                  }}>
                                    Q
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px 15px', verticalAlign: 'top' }}>
                              <span style={{
                                backgroundColor: entry.note_type === 'feedback' ? '#17a2b8' : '#6c757d',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.8rem'
                              }}>
                                {getNoteTypeLabel(entry.note_type)}
                              </span>
                            </td>
                            <td style={{
                              padding: '10px 15px',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              maxWidth: '400px'
                            }}>
                              {entry.note}
                            </td>
                            <td style={{ padding: '10px 15px', verticalAlign: 'top', color: '#666' }}>
                              {formatDate(entry.created_at)}
                            </td>
                          </tr>
                          {expandedEntries.has(entry.id) && entry.questionnaire && (
                            <tr>
                              <td colSpan="4" style={{ padding: '0 15px 15px 15px' }}>
                                {renderQuestionnaire(entry.questionnaire)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiaryEntriesManager;